/**
 * Подмножество markdown для заметок органайзера (эпик 9, стори 9.1).
 *
 * <p>Разбор, а не отрисовка: результат — плоский список кусочков с пометками, а рисует их
 * компонент обычными `Text`. Отсюда и главное свойство безопасности — HTML здесь не появляется
 * ни на одном шаге, поэтому «сырые теги не исполняются» выполняется само собой, а не проверкой
 * входа. На вебе `Text` превращается в `span` с текстовым содержимым, и вставить разметку через
 * тело заметки нечем.
 *
 * <p>Поддержано ровно то, что обещает стори: **жирный**, _курсив_, списки и [ссылки](адрес).
 * Больше — осознанно нет: каждый новый вид разметки это ещё одна пара правил, которые надо
 * согласовать между собой, а заметка себе не документ.
 *
 * <p>ВАЖНО про подпись: {@link noteTitleFallback} — не «ещё один разбор», а зеркало серверного
 * `ChatPreviewCacheService.plainText` (backend/src/main/java/com/vrmessenger/chat). Одну и ту же
 * заметку подписывают обе стороны: сервер — карточку в списке чатов и тело пуша, клиент — чип в
 * полосе закреплённых. Разошедшиеся правила дают у одной заметки два разных названия на соседних
 * экранах, и человек считает это двумя разными заметками. Правила и таблица случаев ниже.
 */

export type NoteInline =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'link'; text: string; url: string };

export type NoteBlock =
  | { kind: 'paragraph'; spans: NoteInline[] }
  | { kind: 'bullet'; spans: NoteInline[] }
  | { kind: 'ordered'; index: number; spans: NoteInline[] };

/**
 * Ссылка принимается только со схемой http(s). `javascript:` и `data:` не проходят: тело
 * заметки человек может вставить откуда угодно, а нажатие по ссылке — это уже действие.
 */
const SAFE_URL = /^https?:\/\/\S+$/i;

const LINK = /\[([^\]\n]*)\]\(([^)\s]+)\)/;
const BOLD = /\*\*([^*\n]+)\*\*/;
/**
 * Курсив — только на границе слова. Без этих проверок `some_var_name` разбирался в
 * «some», курсивное «var», «name»: snake_case, имена файлов и переменных в теле заметки рисовались
 * курсивом, а подчёркивания из них пропадали — то есть скопированное из заметки имя переставало
 * быть тем именем. Серверный разбор подписи (`MD_UNDERSCORE`) защищается от этого ровно так же,
 * и правило обязано быть общим: иначе тело и подпись одной заметки читают её по-разному.
 */
const ITALIC = /(?<![\p{L}\p{N}])_([^_\n]+)_(?![\p{L}\p{N}])/u;

/**
 * Те же правила с флагом `g` — чтобы искать с позиции через `lastIndex`, не нарезая хвост.
 *
 * <p>Нарезать хвост тут нельзя именно из-за курсива: у него есть ретроспективная проверка, и
 * `line.slice(p)` спрятал бы от неё предыдущий символ — в `some_var_name` разбор с позиции 4
 * увидел бы `_var_` в начале строки и снова сделал его курсивом. Поиск с позиции `p` по целой
 * строке эту проверку кормит настоящим соседом.
 */
const LINK_G = new RegExp(LINK.source, 'g');
const BOLD_G = new RegExp(BOLD.source, 'g');
const ITALIC_G = new RegExp(ITALIC.source, 'gu');

/**
 * Сколько символов ОДНОЙ строки идёт в разбор разметки; хвост дальше этого предела попадает в
 * результат целиком, но простым текстом — ничего не теряется, просто не размечается.
 *
 * <p>Защищается время разбора, а не память. Правило ссылки `\[([^\]\n]*)\]\(...` на строке без
 * единой `]` откатывается с каждой стартовой позиции до конца строки, то есть работает
 * квадратично: замер на `'['.repeat(50000)` (предел длины заметки по стори) — 2021 мс в одном
 * `exec`, и это блокирующая работа на JS-потоке, то есть застывший на две секунды экран заметки
 * при каждой перерисовке. На 10 000 символов тот же замер даёт 66 мс. Такая строка — не текст,
 * который человек читает на телефоне, а вставленный блоб; резать разметку в его хвосте дешевле,
 * чем морозить экран.
 *
 * <p>Побочно предел ограничивает и число кусочков в строке, то есть глубину любого построчного
 * прохода по ним.
 */
const INLINE_SCAN_MAX_CHARS = 10_000;

/** Правило разбора: где нашлось в прошлый раз и не кончились ли совпадения вовсе. */
type Rule = {
  re: RegExp;
  make: (m: RegExpExecArray) => NoteInline[];
  match: RegExpExecArray | null;
  exhausted: boolean;
};

/**
 * Разбор одной строки на кусочки. Порядок правил важен: ссылка раньше выделений.
 *
 * <p>Цикл, а не рекурсия по хвосту: глубина стека равнялась бы числу разметочных вставок в
 * строке, и заметка в разрешённые 50 000 символов с частым `**…**` роняла бы разбор с
 * RangeError. Падало бы это внутри рендера строки ленты — то есть вместо ленты человек получал
 * бы красный экран, и после перезапуска тоже: заметка уже лежит на сервере.
 *
 * <p>Хвост при этом не нарезается, а прощупывается через `lastIndex`, и найденное совпадение
 * переиспользуется, пока курсор до него не дошёл: иначе на каждый кусочек приходился бы новый
 * проход по остатку строки, и вместо падения вышли бы секунды на разбор.
 */
export function parseInline(line: string): NoteInline[] {
  if (line.length === 0) return [];
  if (line.length > INLINE_SCAN_MAX_CHARS) {
    // Режем по целому символу: разрыв суррогатной пары дал бы в тексте «ромб с вопросом» на
    // месте эмодзи, а склеить обратно уже нечем — кусочки уходят в разные `Text`. Пару отдаём
    // хвосту, а не голове: сдвиг границы ВПЕРЁД оставил бы голову длиннее предела, и она ушла бы
    // в тот же разрез второй раз — то есть в вечную рекурсию.
    let cut = INLINE_SCAN_MAX_CHARS;
    const code = line.charCodeAt(cut);
    if (code >= 0xdc00 && code <= 0xdfff) cut -= 1;
    const spans = parseInline(line.slice(0, cut));
    spans.push({ kind: 'text', text: line.slice(cut) });
    return spans;
  }

  const rules: Rule[] = [
    { re: LINK_G, make: (m) => inlineLink(m[1], m[2]), match: null, exhausted: false },
    { re: BOLD_G, make: (m) => [{ kind: 'bold', text: m[1] } as NoteInline], match: null, exhausted: false },
    { re: ITALIC_G, make: (m) => [{ kind: 'italic', text: m[1] } as NoteInline], match: null, exhausted: false },
  ];

  const out: NoteInline[] = [];
  let pos = 0;
  while (pos < line.length) {
    let first: Rule | null = null;
    for (const rule of rules) {
      if (rule.exhausted) continue;
      // Совпадение левее курсора уже съедено — ищем следующее. Найденное правее курсора
      // остаётся самым левым и после сдвига, поэтому повторный поиск не нужен.
      if (rule.match == null || rule.match.index < pos) {
        rule.re.lastIndex = pos;
        rule.match = rule.re.exec(line);
        // Раз с этой позиции совпадений нет, дальше по строке их тем более не будет.
        if (rule.match == null) {
          rule.exhausted = true;
          continue;
        }
      }
      // Самый левый — иначе `_курсив_ и **жирный**` разберётся с конца и потеряет начало строки.
      if (first == null || rule.match.index < first.match!.index) first = rule;
    }

    if (first == null) {
      out.push({ kind: 'text', text: line.slice(pos) });
      break;
    }
    const m = first.match!;
    if (m.index > pos) out.push({ kind: 'text', text: line.slice(pos, m.index) });
    out.push(...first.make(m));
    pos = m.index + m[0].length;
  }
  return out;
}

function inlineLink(text: string, url: string): NoteInline[] {
  if (!SAFE_URL.test(url)) {
    // Небезопасный адрес не выкидываем молча: человек написал его сам и должен увидеть, что
    // ссылкой он не стал.
    return [{ kind: 'text', text: `${text} (${url})` }];
  }
  return [{ kind: 'link', text: text.length > 0 ? text : url, url }];
}

/**
 * Разбор тела заметки на блоки. Пустые строки схлопываются: в заметке их ставят для воздуха,
 * а не как отдельный абзац.
 */
export function parseNoteBody(body: string): NoteBlock[] {
  const out: NoteBlock[] = [];
  let orderedCounter = 0;
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (line.trim().length === 0) {
      orderedCounter = 0;
      continue;
    }
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      orderedCounter = 0;
      out.push({ kind: 'bullet', spans: parseInline(bullet[1]) });
      continue;
    }
    const ordered = /^\s*(\d+)[.)]\s+(.*)$/.exec(line);
    if (ordered) {
      // Нумерация берётся своя, а не из текста: человек пишет «1.» на каждой строке чаще, чем
      // считает вручную, и список из пяти единиц выглядел бы опечаткой.
      orderedCounter += 1;
      out.push({ kind: 'ordered', index: orderedCounter, spans: parseInline(ordered[2]) });
      continue;
    }
    orderedCounter = 0;
    out.push({ kind: 'paragraph', spans: parseInline(line) });
  }
  return out;
}

/* --------------------------------------------------------------------------------------------
 * ПОДПИСЬ ЗАМЕТКИ. Зеркало `ChatPreviewCacheService.plainText` / `firstMeaningfulLine`.
 *
 * Правила ниже повторяют серверные один в один и в том же порядке. Порядок не косметика:
 * звёздочки снимаются раньше подчёркиваний, чтобы вложенное `**_важно_**` ушло целиком за один
 * проход каждым правилом.
 *
 * ОБЩАЯ ТАБЛИЦА СЛУЧАЕВ (вход → ожидаемая подпись). Тот же список исполняется в
 * src/util/__tests__/noteMarkdown.test.ts; серверная сторона может сверяться по нему же.
 *
 *   'some_var_name'                        → 'some_var_name'
 *   '# заголовок'                          → 'заголовок'
 *   '> цитата'                             → 'цитата'
 *   '**жирный** и _курсив_'                → 'жирный и курсив'
 *   '[a](https://e.com) [b](https://f.com)'→ 'a b'
 *   '`код`'                                → 'код'
 *   '~~зачёркнуто~~'                       → 'зачёркнуто'
 *   '- пункт'                              → 'пункт'
 *   '1. раз'                               → 'раз'
 *   '**_важно_**'                          → 'важно'
 *   '## **Список**  покупок'               → 'Список покупок'
 *   '_курсив_ и some_var_name'             → 'курсив и some_var_name'
 *   '[тут](javascript:alert(1))'           → 'тут)'
 *
 * Про последнюю строку: подпись — это текст, а не нажимаемая ссылка, поэтому опасный адрес здесь
 * просто снимается вместе с разметкой (в теле заметки им занимается `inlineLink`). Хвостовая
 * скобка остаётся: правило ссылки берёт адрес до ПЕРВОЙ `)`. Так же ведёт себя и сервер — случай
 * оставлен в таблице именно затем, чтобы кто-нибудь не «починил» его на одной стороне.
 * -------------------------------------------------------------------------------------------- */

/** `[текст](ссылка)` → `текст`: в подписи от URL толку нет. Флаг `g` — иначе снимется только
 *  первая ссылка, и строка из двух ссылок доезжала до чипа и до пуша как `a [b](https://f.com)`. */
const MD_LINK_ALL = /\[([^\]]*)\]\([^)]*\)/g;

/** Маркеры в начале строки: заголовок, цитата, пункт списка — нумерованный и обычный. */
const MD_LEADING_MARKER = /^\s*(?:[>#]+\s*|[-*+]\s+|\d+[.)]\s+)+/;

/** Парные звёздочки, тильды и обратные кавычки: `**жирный**`, `` `код` ``. */
const MD_EMPHASIS = /(\*{1,2}|~~|`)(.+?)\1/g;

/**
 * Подчёркивания — отдельно и только на границе слова. Парности мало: в `some_var_name`
 * подчёркивания тоже парные, и общее правило превратило бы его в `somevarname`.
 */
const MD_UNDERSCORE = /(?<![\p{L}\p{N}])(_{1,2})(.+?)\1(?![\p{L}\p{N}])/gu;

/**
 * Сколько символов строки вообще попадает в разбор разметки. Запас к `limit` — на снимаемые знаки
 * разметки; остальное всё равно не видно. Число то же, что серверный `NOTE_SCAN_MAX_CHARS`:
 * если резать на разной длине, у строки с разметкой на границе стороны разойдутся в подписи.
 */
const NOTE_SCAN_MAX_CHARS = 400;

/**
 * Первая строка тела — заголовок по умолчанию, когда своего нет.
 *
 * <p>Правила снятия разметки общие с сервером (таблица выше), а вот `limit` — нет: у сервера это
 * 120 символов на карточку списка чатов, здесь 60 на чип закреплённых, который вдвое уже. Длина —
 * свойство места, где подпись показывают; расходиться не должны именно правила разбора.
 */
export function noteTitleFallback(body: string, limit = 60): string {
  // Перебираем строки до первой, от которой после снятия разметки что-то осталось: строка из
  // одних маркеров («---», «> ») названием быть не может, а серверная сторона идёт так же.
  for (const line of body.split(/\r\n|[\n\r]/)) {
    // Режем строку ДО разбора, а не после: тело заметки доходит до 50 000 символов, и гонять по
    // нему регулярки ради полусотни символов подписи — работа впустую, причём на каждой
    // перерисовке полосы закреплённых.
    const head = line.length > NOTE_SCAN_MAX_CHARS ? line.slice(0, NOTE_SCAN_MAX_CHARS) : line;
    const plain = plainText(head);
    if (plain.length > 0) return clip(plain, limit);
  }
  return '';
}

/**
 * Снимает разметку. Тело заметки — markdown-подмножество, и в подписи оно читается как мусор:
 * чип показал бы `## **Список** покупок` вместо «Список покупок». Рендерить markdown тут нечем и
 * незачем — в подписи одна строка без начертаний.
 */
function plainText(markdown: string): string {
  if (markdown.length === 0) return '';
  let s = markdown.replace(MD_LINK_ALL, '$1');
  s = s.replace(MD_LEADING_MARKER, '');
  // Звёздочки раньше подчёркиваний: так вложенное начертание **_важно_** снимается целиком
  // за один проход каждым правилом.
  s = s.replace(MD_EMPHASIS, '$2');
  s = s.replace(MD_UNDERSCORE, '$2');
  // `\s` в JS шире, чем в Java (сюда попадает и неразрывный пробел). Расхождение в сторону
  // клиента и безобидное: подпись всё равно схлопывается в одну строку.
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * Обрезает подпись. Тело заметки по стори доходит до 50 000 символов, и целиком оно поехало бы
 * в чип, где видно от силы строку. Многоточие добавляется СВЕРХ предела — как на сервере, иначе
 * одна и та же заметка обрывалась бы на разных символах.
 */
function clip(text: string, limit: number): string {
  if (text.length <= limit) return text;
  let head = text.slice(0, limit);
  const lastSpace = head.lastIndexOf(' ');
  // Рвать посреди слова некрасиво, но и оставлять два символа от длинного слова хуже,
  // чем обрезать по букве.
  if (lastSpace > limit / 2) head = head.slice(0, lastSpace);
  return `${head.replace(/\s+$/, '')}…`;
}
